import { PublicUserLabel } from '@affine/core/modules/cloud/views/public-user';
import clsx from 'clsx';
import { type CSSProperties, useMemo } from 'react';

import * as styles from './document-presence-avatar-stack.css';

export function DocumentPresenceAvatarStack({
  userIds,
  moderatorUserId,
  size = 16,
  maxVisible = 4,
  className,
}: {
  userIds: readonly string[] | null | undefined;
  moderatorUserId?: string;
  size?: number;
  maxVisible?: number;
  className?: string;
}) {
  const ordered = useMemo(() => {
    const unique = Array.from(new Set(userIds ?? []));
    return moderatorUserId && unique.includes(moderatorUserId)
      ? [moderatorUserId, ...unique.filter(id => id !== moderatorUserId)]
      : unique;
  }, [moderatorUserId, userIds]);

  if (ordered.length === 0) return null;

  const visible = ordered.slice(0, maxVisible);
  const overflowCount = ordered.length - visible.length;
  const style = {
    '--document-presence-size': `${size}px`,
    '--document-presence-overlap': `${Math.max(3, Math.round(size * 0.22))}px`,
  } as CSSProperties;

  return (
    <div
      className={clsx(styles.stack, className)}
      style={style}
      data-testid="doc-presence-avatar-stack"
    >
      {visible.map(userId => (
        <span
          key={userId}
          className={clsx(
            styles.avatar,
            userId === moderatorUserId && styles.moderator
          )}
          style={{ width: size, height: size }}
        >
          <PublicUserLabel id={userId} size={size} showName={false} />
        </span>
      ))}
      {overflowCount > 0 ? (
        <span className={styles.overflow}>+{overflowCount}</span>
      ) : null}
    </div>
  );
}
